export interface IColorMap {
    [key: string]: string;
}

export const Theme1Colors = {
    // Base
    White: "#fff",
    Black: "#000",
    LightGray: "#D3D3D3",

    // Primary (base: #356200)
    PrimaryTextEmphasis: "#1f3700",
    PrimaryDark: "#2e5200",
    Primary: "#356200",
    PrimaryMid: "#5d7e26",
    PrimaryBorderSubtle: "#92a764",
    PrimaryBgSubtle: "#c6d1a3",
    PrimaryVeryLight: "#f0f4e6",
    PrimaryVeryLight2: "#e9efd9",
    PrimaryVeryLight3: "#f1f5e8",
    PrimaryAlpha10: "rgba(53, 98, 0, 0.1)",
    PrimaryAlpha30: "rgba(53, 98, 0, 0.3)",
    PrimaryAlpha90: "rgba(53, 98, 0, 0.9)",

    // Secondary (base: #765070)
    SecondaryTextEmphasis: "#3d293a",
    SecondaryDark: "#5f3f5a",
    Secondary: "#765070",
    SecondaryMid: "#916a8c",
    SecondaryBorderSubtle: "#a6879f",
    SecondaryBgSubtle: "#c8b2c1",
    SecondaryVeryLight: "#f4edf2",
    SecondaryAlpha10: "rgba(118, 80, 112, 0.1)",
    SecondaryAlpha30: "rgba(118, 80, 112, 0.3)",
    SecondaryAlpha90: "rgba(118, 80, 112, 0.9)",

    // Tertiary (base: #579e03)
    TertiaryTextEmphasis: "#244000",
    TertiaryDark: "#3f7202",
    Tertiary: "#579e03",
    TertiaryMid: "#85c047ff",
    TertiaryBorderSubtle: "#adcf86ff",
    TertiaryBgSubtle: "#cbddb6ff",
    TertiaryVeryLight: "#e4e7df",
    TertiaryAlpha10: "rgba(87, 158, 3, 0.1)",
    TertiaryAlpha30: "rgba(87, 158, 3, 0.3)",
    TertiaryAlpha90: "rgba(87, 158, 3, 0.9)",

    // Warning
    WarningTextEmphasis: "rgb(114, 57, 0)",
    WarningDark: "rgb(214, 127, 45)",
    Warning: "rgb(252, 155, 65)",
    WarningMid: "rgb(255, 185, 120)",
    WarningBorderSubtle: "rgb(255, 207, 162)",
    WarningBgSubtle: "rgb(255, 234, 214)",
    WarningVeryLight: "rgb(252, 246, 241)",
    WarningAlpha10: "rgba(252, 155, 65, .1)",
    WarningAlpha30: "rgba(252, 155, 65, .3)",
    WarningAlpha90: "rgba(252, 155, 65, .9)",

    // Info
    InfoTextEmphasis: "rgb(43, 36, 104)",
    InfoDark: "rgb(66, 57, 148)",
    Info: "rgb(85, 75, 178)",
    InfoMid: "rgb(126, 118, 204)",
    InfoBorderSubtle: "rgb(165, 160, 207)",
    InfoBgSubtle: "rgb(199, 196, 223)",
    InfoVeryLight: "rgb(240, 239, 252)",
    InfoAlpha10: "rgba(85, 75, 178, .1)",
    InfoAlpha30: "rgba(85, 75, 178, .3)",
    InfoAlpha90: "rgba(85, 75, 178, .9)",

    // Danger
    DangerTextEmphasis: "rgb(112, 42, 42)",
    DangerDark: "rgb(156, 43, 43)",
    Danger: "rgb(189, 46, 46)",
    DangerMid: "rgb(207, 84, 84)",
    DangerBorderSubtle: "rgb(206, 169, 169)",
    DangerBgSubtle: "rgb(233, 208, 208)",
    DangerVeryLight: "rgb(253, 244, 244)",
    DangerAlpha10: "rgba(189, 46, 46, .1)",
    DangerAlpha30: "rgba(189, 46, 46, .3)",
    DangerAlpha90: "rgba(189, 46, 46, .9)",

    // Success
    SuccessTextEmphasis: "rgb(0, 61, 27)",
    SuccessDark: "rgb(0, 145, 65)",
    Success: "rgb(1, 181, 82)",
    SuccessMid: "rgb(47, 201, 116)",
    SuccessBorderSubtle: "rgb(74, 196, 129)",
    SuccessBgSubtle: "rgb(150, 219, 181)",
    SuccessVeryLight: "rgb(244, 255, 249)",
    SuccessAlpha10: "rgba(1, 181, 82, .1)",
    SuccessAlpha30: "rgba(1, 181, 82, .3)",
    SuccessAlpha90: "rgba(1, 181, 82, .9)",
} as const;

export const ChartColors = [
    "#356200",
    "#79A2B8",
    "#579E03",
    "#765070",
    "#95C500",
    "#D3DF37",
    "#E9EAE3",
    "#58595B",
    "#cef563ff",
    "#b343a2ff",
    "#c86becff",
];

export const generateAppThemeProps = (colors: IColorMap) => {
    return Object.entries(colors).map(([key, value]) => {
        let cssVarName = "--";

        const prefixes = ["Primary", "Secondary", "Warning", "Info", "Danger", "Success", "Tertiary"];
        const foundPrefix = prefixes.find(p => key.startsWith(p));

        if (foundPrefix) {
            const suffix = key.slice(foundPrefix.length); // Remove prefix from key
            cssVarName += `bs-${foundPrefix.toLowerCase()}`;

            if (!suffix) {
                // e.g., "Primary" → "--bs-primary"
            } else if (/^Alpha\d+$/.test(suffix)) {
                cssVarName += `-alpha-${suffix.slice(5)}`;
            } else {
                // Convert e.g., BorderSubtle → border-subtle
                const suffixKebab = suffix.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
                cssVarName += `-${suffixKebab}`;
            }
        } else {
            // fallback like White → --white, LightGray → --light-gray
            cssVarName += key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
        }

        return { name: cssVarName, value };
    });
};
